import { batch } from 'solid-js';
import { TriggerCache } from '@solid-primitives/trigger';

// src/index.ts
var $KEYS = Symbol("track-keys");
var ReactiveSet = class extends Set {
  #triggers = new TriggerCache();
  constructor(values) {
    super();
    if (values) for (const v of values) super.add(v);
  }
  [Symbol.iterator]() {
    return this.values();
  }
  get size() {
    this.#triggers.track($KEYS);
    return super.size;
  }
  has(value) {
    this.#triggers.track(value);
    return super.has(value);
  }
  keys() {
    return this.values();
  }
  *values() {
    this.#triggers.track($KEYS);
    for (const value of super.values()) {
      yield value;
    }
  }
  *entries() {
    this.#triggers.track($KEYS);
    for (const entry of super.entries()) {
      yield entry;
    }
  }
  forEach(callbackfn, thisArg) {
    this.#triggers.track($KEYS);
    super.forEach(callbackfn, thisArg);
  }
  add(value) {
    if (!super.has(value)) {
      super.add(value);
      batch(() => {
        this.#triggers.dirty(value);
        this.#triggers.dirty($KEYS);
      });
    }
    return this;
  }
  delete(value) {
    const result = super.delete(value);
    if (result) {
      batch(() => {
        this.#triggers.dirty(value);
        this.#triggers.dirty($KEYS);
      });
    }
    return result;
  }
  clear() {
    if (super.size) {
      super.clear();
      batch(() => {
        this.#triggers.dirtyAll();
      });
    }
  }
};
var ReactiveWeakSet = class extends WeakSet {
  #triggers = new TriggerCache(WeakMap);
  constructor(values) {
    super();
    if (values) for (const v of values) super.add(v);
  }
  has(value) {
    this.#triggers.track(value);
    return super.has(value);
  }
  add(value) {
    if (!super.has(value)) {
      super.add(value);
      this.#triggers.dirty(value);
    }
    return this;
  }
  delete(value) {
    const result = super.delete(value);
    result && this.#triggers.dirty(value);
    return result;
  }
};
function createSet(initial) {
  const set = new ReactiveSet(initial);
  return new Proxy(() => [...set], {
    get: (_, b) => set[b]
  });
}
function createWeakSet(initial) {
  return new ReactiveWeakSet(initial);
}

export { ReactiveSet, ReactiveWeakSet, createSet, createWeakSet };
